/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { OfficeDetailsDto, OfficeImportTemplateDto } from '@gh/builder-shared-data';
import { getDefaultEmail, getEmailLocalName } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { cast } from '@gh/core-util';

import { concat, forEach, keyBy, mapValues, omit, pick, without } from 'lodash';

export type OfficeDetails = OfficeDetailsDto & {
  applyLicense: boolean;
  templateFlatHierarchy: Hash<OfficeImportTemplate>;
  sourceMasterZone?: any;
};
export type OfficeImportTemplate = {
  name: string;
  checked: boolean;
  parentName?: string;
  childrenNames: string[];
  nextNames: string[];
  previousNames: string[];
};

export function isParentOfficeImportTemplate(template: OfficeImportTemplate): boolean {
  return template.childrenNames.length > 0;
}

export function officeDetailsToDto(details: OfficeDetails): OfficeDetailsDto {
  const sourceOffice = details.sourceOffice && details.sourceOffice.officeId ? details.sourceOffice : null;
  return cast<OfficeDetailsDto>(omit({
    ...omit(details, ['templateFlatHierarchy', 'sourceMasterZone']),
    sourceOffice,
    masterZone: pick(details.masterZone, ['active', 'code', 'id', 'name']),
    licenseNo: details.applyLicense ? details.licenseNo : void 0,
    email: details.email && getDefaultEmail(details.email),
    templates: details.templates.map((rawTemplate) => {
      const template = details.templateFlatHierarchy[rawTemplate.name];
      return {
        ...rawTemplate,
        checked: isParentOfficeImportTemplate(template)
          ? template.childrenNames.some((name) => details.templateFlatHierarchy[name].checked)
          : template.checked,
      };
    }),
  },                                 'applyLicense'));
}

export function officeDetailsFromDto(details: OfficeDetailsDto): OfficeDetails {
  return {
    ...details,
    applyLicense: !!details.licenseNo,
    email: details.email && getEmailLocalName(details.email),
    templateFlatHierarchy: buildTemplateFlatHierarchy(details.templates),
  };
}

/**
 * Builds convenient data structure in order to traverse template hierarchy when some of templates is checked/unchecked.
 * @param rawTemplates
 */
export const buildTemplateFlatHierarchy = (rawTemplates: OfficeImportTemplateDto[]): Hash<OfficeImportTemplate> => {
  const rawTemplatesByName = keyBy(rawTemplates, 'name');
  const rawTemplatesById = keyBy(rawTemplates, 'id');
  const childrenNamesById: Record<string | number, string[]> = {};
  const previousNamesById: Record<string | number, string[]> = {};
  rawTemplates
    .forEach(({ id, parentId, dependIds, name }) => {
      forEach(dependIds, (dependId) => {
        previousNamesById[dependId] = concat(previousNamesById[dependId] || [], name);
      });
      if (parentId) {
        childrenNamesById[parentId] = concat(childrenNamesById[parentId] || [], name);
      }
    });
  return mapValues(rawTemplatesByName, (rawTemplate) => ({
    name: rawTemplate.name,
    checked: rawTemplate.checked,
    parentName: rawTemplate.parentId ? rawTemplatesById[rawTemplate.parentId].name : void 0,
    childrenNames: childrenNamesById[rawTemplate.id] || [],
    nextNames: without(rawTemplate.dependIds, <ID>rawTemplate.parentId)
      .map((id) => rawTemplatesById[id])
      .filter(Boolean)
      .map((template) => template.name),
    previousNames: previousNamesById[rawTemplate.id] || [],
  }));
};
